import { useEffect, useState } from "react";
import { ApiClientError, fetchReviewItem, fetchReviewList, fetchReviewRules } from "../../api/client";
import type {
  ReviewItem,
  ReviewItemDetailResponse,
  ReviewListFilter,
  ReviewOverviewResponse,
  ReviewPeriodMode,
  ReviewRule,
  ReviewStoreFilter,
} from "../../types/reviews";
import { StateBanner } from "../../components/StateBanner";
import { RulesCard } from "./components/RulesCard";
import { ReviewListPanel } from "./components/ReviewListPanel";
import { ReviewDetail } from "./components/ReviewDetail";

const FILTER_OPTIONS: { key: ReviewListFilter; label: string }[] = [
  { key: "unreplied", label: "未返信" },
  { key: "queued", label: "投稿待ち" },
  { key: "replied", label: "返信済み" },
  { key: "all", label: "すべて" },
  { key: "cause", label: "原因分類待ち" },
  { key: "resp", label: "個別対応待ち" },
  { key: "improve", label: "再発防止待ち" },
  { key: "overdue", label: "期限超過" },
  { key: "done", label: "完了" },
];

type Loadable<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ok"; data: T };

interface ResponseTabProps {
  period: ReviewPeriodMode;
  month: string;
  store: ReviewStoreFilter;
  filter: ReviewListFilter;
  onFilterChange: (f: ReviewListFilter) => void;
  overview: ReviewOverviewResponse | null;
  onGoToFilter: (f: ReviewListFilter) => void;
}

export function ResponseTab({ period, month, store, filter, onFilterChange, overview }: ResponseTabProps) {
  const [rules, setRules] = useState<ReviewRule[] | null>(null);
  const [owners, setOwners] = useState<string[]>(["未割当"]);
  const [list, setList] = useState<Loadable<ReviewItem[]>>({ status: "loading" });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<Loadable<ReviewItemDetailResponse> | null>(null);

  useEffect(() => {
    fetchReviewRules()
      .then((res) => {
        setRules(res.rules);
        setOwners(res.owners);
      })
      .catch(() => undefined);
  }, []);

  const reloadList = () => {
    setList({ status: "loading" });
    fetchReviewList({ period, month, store, filter })
      .then((res) => {
        setList({ status: "ok", data: res.items });
        setSelectedKey((cur) => (cur && res.items.some((i) => i.key === cur) ? cur : res.items[0]?.key ?? null));
      })
      .catch((e: unknown) => setList({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  };

  useEffect(() => {
    reloadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, month, store, filter]);

  useEffect(() => {
    if (!selectedKey) {
      setDetail(null);
      return;
    }
    setDetail({ status: "loading" });
    fetchReviewItem(selectedKey)
      .then((res) => setDetail({ status: "ok", data: res }))
      .catch((e: unknown) => setDetail({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [selectedKey]);

  const items = list.status === "ok" ? list.data : [];

  const patchItemLocally = (updated: ReviewItem) => {
    setList((cur) => (cur.status === "ok" ? { status: "ok", data: cur.data.map((i) => (i.key === updated.key ? updated : i)) } : cur));
    setDetail((cur) => (cur && cur.status === "ok" ? { status: "ok", data: { ...cur.data, item: updated } } : cur));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {rules && <RulesCard rules={rules} replyStats={overview?.replyStats ?? null} onChanged={setRules} />}

      <div className="rv-card">
        <span className="rv-card-title">確実対応フロー</span>
        {overview ? (
          <div className="rv-pipeline">
            <button className="rv-pipeline-seg" onClick={() => onFilterChange("cause")}>
              <span className="rv-pipeline-bar" style={{ background: "var(--rv-bad)", width: "100%" }} />
              <span className="rv-pipeline-value">{overview.low.open}</span>
              <span className="rv-pipeline-label">原因分類待ち</span>
            </button>
            <button className="rv-pipeline-seg" onClick={() => onFilterChange("resp")}>
              <span className="rv-pipeline-bar" style={{ background: "var(--rv-warn-light)", width: "100%" }} />
              <span className="rv-pipeline-value">{overview.low.wip}</span>
              <span className="rv-pipeline-label">個別対応・再発防止待ち</span>
            </button>
            <button className="rv-pipeline-seg" onClick={() => onFilterChange("overdue")}>
              <span className="rv-pipeline-bar" style={{ background: "var(--rv-bad)", width: "100%" }} />
              <span className="rv-pipeline-value">{overview.low.overdue}</span>
              <span className="rv-pipeline-label">期限超過</span>
            </button>
            <button className="rv-pipeline-seg" onClick={() => onFilterChange("done")}>
              <span className="rv-pipeline-bar" style={{ background: "var(--rv-good)", width: "100%" }} />
              <span className="rv-pipeline-value">{overview.low.done}</span>
              <span className="rv-pipeline-label">完了</span>
            </button>
          </div>
        ) : (
          <div className="rv-empty">読み込み中…</div>
        )}
      </div>

      <div className="rv-filter-chips">
        {FILTER_OPTIONS.map((f) => (
          <button key={f.key} className={`rv-filter-chip ${filter === f.key ? "active" : ""}`} onClick={() => onFilterChange(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="rv-list-detail">
        <div className="rv-card">
          <span className="rv-card-title">一覧（{items.length}件）</span>
          {list.status === "loading" && <StateBanner kind="loading" message="読み込み中" />}
          {list.status === "error" && <StateBanner kind="error" message={list.message} onRetry={reloadList} />}
          {list.status === "ok" && <ReviewListPanel items={items} selectedKey={selectedKey} onSelect={setSelectedKey} />}
        </div>

        <div className="rv-card">
          <span className="rv-card-title">詳細</span>
          {!selectedKey && <div className="rv-empty">左の一覧からレビューを選択してください。</div>}
          {detail?.status === "loading" && <StateBanner kind="loading" message="読み込み中" />}
          {detail?.status === "error" && <StateBanner kind="error" message={detail.message} />}
          {detail?.status === "ok" && (
            <ReviewDetail item={detail.data.item} owners={detail.data.owners.length ? detail.data.owners : owners} onChanged={patchItemLocally} />
          )}
        </div>
      </div>
    </div>
  );
}
